import { ref, onMounted } from 'vue'
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '../stores/auth'
import { mockAppUpdate } from '../mocks/appUpdates'

interface Feature {
  id: string
  title: string
  description: string
  icon: string
  type: 'new' | 'improvement' | 'fix'
}

interface AppUpdate {
  id: string
  version: string
  title: string
  description: string
  releaseDate: Date
  features: Feature[]
  priority: 'low' | 'medium' | 'high'
  showConfetti: boolean
}

export const useAppUpdates = (useMockData: boolean = true) => {
  const update = ref<AppUpdate | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const showUpdateModal = ref(false)
  const db = FirebaseProvider.getInstance().getFirestore()
  const authStore = useAuthStore()

    const getUserVersion = async () => {
      if (!authStore.user) return null;
    
      try {
        const userRef = doc(db, "Users", authStore.user.uid);
        const userDoc = await getDoc(userRef);
    
        if (userDoc.exists()) {
          return userDoc.data()?.appVersion || null;
        } else {
          // Si el documento no existe, lo creamos con un valor por defecto
          const defaultVersion = mockAppUpdate.version; // Cambia esto según la versión por defecto
          await setDoc(userRef, { appVersion: defaultVersion }, { merge: true });
          return defaultVersion;
        }
      } catch (err) {
        // undefined = no se pudo saber. null significaría "el usuario nunca la
        // vio" y abría el modal: en pestaña fría una lectura que falla se
        // disfrazaba de usuario nuevo.
        console.error("Error getting user version:", err);
        return undefined;
      }
    }
  const checkForUpdates = async () => {
    if (!authStore.user) return
    // $subscribe dispara en cada mutación del store: una verificación a la vez.
    if (isLoading.value) return

    isLoading.value = true
    error.value = null

    try {
        update.value = {
          ...mockAppUpdate,
          releaseDate: new Date(mockAppUpdate.releaseDate)
        }
        
        // Verificar versión del usuario en Firestore
        const savedVersion = await getUserVersion()
        if (savedVersion === undefined) return
        if (!savedVersion || savedVersion !== update.value.version) {
          showUpdateModal.value = true
        }
      
    } catch (err: any) {
      console.error('Error checking for updates:', err)
      error.value = err.message
    } finally {
      isLoading.value = false
    }
  }

  const markUpdateAsViewed = async () => {
    if (!update.value || !authStore.user) return

    try {
      // Actualizar la versión en el documento del usuario
      const userRef = doc(db, 'Users', authStore.user.uid)
      await updateDoc(userRef, {
        appVersion: update.value.version,
        lastUpdateView: new Date()
      })

    } catch (err) {
      console.error('Error marking update as viewed:', err)
    }

    showUpdateModal.value = false
  }

  // Observar cambios en el estado de autenticación
  authStore.$subscribe((mutation, state) => {
    if (state.user) {
      checkForUpdates()
    }
  })

  // Verificar actualizaciones cuando el componente se monta
  onMounted(() => {
    if (authStore.user) {
      checkForUpdates()
    }
  })

  return {
    update,
    isLoading,
    error,
    showUpdateModal,
    getUserVersion,
    checkForUpdates,
    markUpdateAsViewed
  }
}
